import { useId, useState } from "react";
import {
    domAnimation,
    LazyMotion,
    m,
    type TargetAndTransition,
    useAnimationControls,
    useReducedMotion,
} from "framer-motion";
import { ProviderType } from "@wags/catalog/pure.ts";
import { Tooltip, TooltipContent, TooltipTrigger } from "@wags/ui/tooltip.tsx";
import {
    BURN,
    EDGE,
    GoldMetalDefs,
    GoldSheen,
    goldRef,
    goldUrl,
} from "./gold-metal";

const WORD_JOINER = "\u2060";

const BONE_PATH =
    "M 72,32 C 96,28 116,22 130,22 C 144,22 164,28 188,32 C 198,28 206,16 216,14 C 230,12 242,24 242,36 C 242,42 234,46 228,48 C 222,50 222,54 228,56 C 234,58 242,62 242,68 C 242,80 230,90 216,88 C 206,86 198,74 188,70 L 72,70 C 62,74 54,86 44,88 C 30,90 18,80 18,68 C 18,62 26,58 32,56 C 38,54 38,50 32,48 C 26,46 18,42 18,36 C 18,24 30,12 44,14 C 54,16 62,28 72,32 Z";

const SWING: TargetAndTransition = {
    rotate: [0, -14, 9, -4, 1.5, 0],
    transition: { duration: 0.8, ease: "easeOut" },
};

const PROVIDER_NOUN: Record<ProviderType, string> = {
    [ProviderType.Breeder]: "breeder",
    [ProviderType.ShelterRescue]: "rescue",
};

export function VerifiedProviderBadge({
    isVerified,
    providerType,
    interactive = false,
}: {
    isVerified: boolean | null | undefined;
    providerType: ProviderType;
    interactive?: boolean;
}) {
    const id = useId().replace(/[^\w-]/g, "");
    if (isVerified !== true) return null;

    const label = `Verified ${PROVIDER_NOUN[providerType]}`;
    if (!interactive) {
        return (
            <>
                {WORD_JOINER}
                <span
                    role="img"
                    aria-label={label}
                    className="relative top-[0.06em] ml-[0.22em] inline-block h-[1cap] w-[max(2.133em,64px)] shrink-0 align-baseline"
                >
                    <span className="absolute inset-x-0 top-1/2 h-[max(0.8em,24px)] -translate-y-1/2">
                        <BoneSvg id={id} animate={false} />
                    </span>
                </span>
            </>
        );
    }

    return <InteractiveVerifiedProviderBadge id={id} label={label} />;
}

function InteractiveVerifiedProviderBadge({
    id,
    label,
}: {
    id: string;
    label: string;
}) {
    const [open, setOpen] = useState(false);
    const swing = useAnimationControls();
    const reduceMotion = useReducedMotion();

    return (
        <LazyMotion features={domAnimation}>
            {WORD_JOINER}
            <Tooltip open={open} onOpenChange={setOpen}>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        aria-label={label}
                        onPointerDown={(event) => {
                            if (event.pointerType === "touch") {
                                event.preventDefault();
                            }
                        }}
                        onClick={() => {
                            setOpen(!open);
                            if (!reduceMotion) void swing.start(SWING);
                        }}
                        className="relative top-[0.06em] ml-[0.22em] inline-block h-[1cap] w-[max(2.133em,64px)] shrink-0 rounded-full align-baseline outline-offset-2 outline-chocolate-lab focus-visible:outline-2 before:absolute before:-inset-2"
                    >
                        <m.span
                            animate={swing}
                            style={{ transformOrigin: "50% 11.111%" }}
                            className="absolute inset-x-0 top-1/2 h-[max(0.8em,24px)] -translate-y-1/2"
                        >
                            <BoneSvg id={id} animate={!reduceMotion} />
                        </m.span>
                    </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={6}>
                    {label}
                </TooltipContent>
            </Tooltip>
        </LazyMotion>
    );
}

function BoneSvg({ id, animate }: { id: string; animate: boolean }) {
    const url = (name: string) => goldUrl(id, name);
    return (
        <svg
            viewBox="10 6 240 90"
            aria-hidden="true"
            className="block size-full overflow-visible"
        >
            <GoldMetalDefs
                id={id}
                path={BONE_PATH}
                gradient={{ y1: 8, y2: 92 }}
                shadow={{
                    x: "-8%",
                    y: "-4%",
                    width: "116%",
                    height: "114%",
                    dy: 1.5,
                    blur: 2,
                    opacity: 0.12,
                }}
            />
            <defs>
                <filter
                    id={goldRef(id, "laser")}
                    x="-10%"
                    y="-30%"
                    width="120%"
                    height="160%"
                >
                    <feOffset
                        in="SourceAlpha"
                        dy="0.65"
                        result="recessOffset"
                    />
                    <feComposite
                        in="SourceAlpha"
                        in2="recessOffset"
                        operator="out"
                        result="recessEdge"
                    />
                    <feFlood
                        floodColor="var(--doberman-black)"
                        floodOpacity="0.45"
                    />
                    <feComposite
                        in2="recessEdge"
                        operator="in"
                        result="recessShadow"
                    />
                    <feDropShadow
                        in="SourceGraphic"
                        dx="0"
                        dy="0.75"
                        stdDeviation="0.2"
                        floodColor="var(--true-white)"
                        floodOpacity="0.65"
                        result="engraving"
                    />
                    <feMerge>
                        <feMergeNode in="engraving" />
                        <feMergeNode in="recessShadow" />
                    </feMerge>
                </filter>
            </defs>
            <path
                d={BONE_PATH}
                fill={url("metal")}
                stroke={EDGE}
                strokeWidth="1"
                filter={url("shadow")}
            />
            {animate && (
                <GoldSheen
                    id={id}
                    width={50}
                    height={100}
                    from={-60}
                    to={300}
                />
            )}
            <circle cx="130" cy="28" r="4" fill={BURN} opacity="0.3" />
            <circle
                cx="130"
                cy="16"
                r="9"
                fill="none"
                stroke={EDGE}
                strokeWidth="2.5"
            />
            <text
                x="130"
                y="59"
                textAnchor="middle"
                fontSize="26"
                fontStyle="normal"
                fontFamily="var(--font-family-heading)"
                fill={BURN}
                filter={url("laser")}
            >
                Verified
            </text>
        </svg>
    );
}
